/**
 * CalibrationHealthBanner — admin-only calm visibility surface.
 *
 * Operator brief (Feb 13, 2026 — Phase D visibility):
 *   "Pure visibility layer only. Show a calm banner only when
 *    drift is detected or status is unknown."
 *
 * Operator extension (May 14, 2026 — Operational Continuity self-
 * monitoring):
 *   "Add the tiny weekly digest line to the existing admin
 *    Calibration Health banner. Show a calm operational signal
 *    such as 'Operational sweepers cleared N stuck operations this
 *    week. Cutoffs: 60 min reviews, 4 h lessons.' If count is 0,
 *    show calm wording like 'No stuck operations swept this week.'"
 *
 * The banner now serves two purposes:
 *   1. AI calibration drift surfacing (existing behaviour — unchanged
 *      contract; renders only on drift / unknown).
 *   2. Operational sweeper weekly digest — always renders for admins
 *      so they can see at a glance that the never-ghost rule is
 *      active and counted.
 *
 * Contract:
 *   - Mounts on the admin dashboard.
 *   - Renders nothing if BOTH the calibration state is healthy AND
 *     the weekly digest endpoint is unreachable (silent fail — banner
 *     never breaks the dashboard).
 *   - Otherwise renders ONE calm container; calibration drift sits
 *     above the operational digest line when both are present.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, Clock, ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function formatTimestamp(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function CalibrationHealthBanner() {
  const [health, setHealth] = useState(null);
  const [digest, setDigest] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Two independent fetches — neither blocks the other; either can
      // fail silently. The banner only goes silent if BOTH go silent.
      const [healthRes, digestRes] = await Promise.allSettled([
        axios.get(`${API}/api/admin/calibration-health`, { withCredentials: true }),
        axios.get(`${API}/api/admin/operations/stuck`, { withCredentials: true }),
      ]);
      if (cancelled) return;
      setHealth(healthRes.status === "fulfilled" ? healthRes.value.data : null);
      setDigest(digestRes.status === "fulfilled" ? digestRes.value.data : null);
      setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, []);

  if (!loaded) return null;

  const status = health?.status;
  const showDrift = status === "drift_detected" || status === "unknown";
  const showDigest = !!digest;

  // If we have neither a drift signal nor a digest payload, render
  // nothing — the dashboard stays clean.
  if (!showDrift && !showDigest) return null;

  const severe = health?.alert?.severity === "severe";
  const lastChecked = formatTimestamp(health?.last_checked);

  // Tone palette:
  //   • drift_detected / unknown → amber (drift) or slate (unknown)
  //   • drift-free + digest-only  → calm slate (lower-priority info)
  let toneCls;
  if (status === "unknown") {
    toneCls = "border-slate-500/30 bg-slate-500/10 text-slate-700 dark:text-slate-200";
  } else if (showDrift) {
    toneCls = severe
      ? "border-amber-500/50 bg-amber-500/15 text-amber-900 dark:text-amber-100"
      : "border-amber-500/30 bg-amber-500/10 text-amber-900 dark:text-amber-100";
  } else {
    toneCls = "border-slate-500/20 bg-slate-500/5 text-slate-700 dark:text-slate-300";
  }
  const iconCls = showDrift
    ? "text-amber-600 dark:text-amber-300"
    : "text-slate-500 dark:text-slate-400";

  return (
    <div
      data-testid="calibration-health-banner"
      data-status={status || "no-drift"}
      data-severity={severe ? "severe" : "mild"}
      className={`rounded-2xl border px-4 py-3 ${toneCls}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-black/10 flex items-center justify-center flex-shrink-0">
          {showDrift ? (
            <AlertTriangle className={`w-4 h-4 ${iconCls}`} />
          ) : (
            <ShieldCheck className={`w-4 h-4 ${iconCls}`} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest font-extrabold opacity-80">
            Admin · platform health
          </p>
          {showDrift && (
            <DriftSection
              status={status}
              alert={health.alert}
              lastChecked={lastChecked}
            />
          )}
          {showDigest && (
            <OperationalDigestLine digest={digest} compact={showDrift} />
          )}
        </div>
      </div>
    </div>
  );
}

function DriftSection({ status, alert, lastChecked }) {
  const title = "AI calibration needs review";
  const subtitle = status === "unknown"
    ? "The calibration alert file is present but could not be parsed. Please check the server logs."
    : "The nightly calibration detected behaviour drift. Review before relying on new AI verdicts.";
  return (
    <>
      <p className="text-sm font-extrabold leading-tight mt-0.5">
        {title}
      </p>
      <p className="text-[12px] leading-snug mt-1 opacity-90">
        {subtitle}
      </p>
      {alert && <CalibrationStats alert={alert} />}
      {lastChecked && (
        <p
          data-testid="calibration-banner-last-checked"
          className="text-[11px] mt-2 opacity-75 inline-flex items-center gap-1"
        >
          <Clock className="w-3 h-3" /> Last checked {lastChecked}
        </p>
      )}
    </>
  );
}

/**
 * Tiny one-line operational sweeper digest. Reuses the
 * /api/admin/operations/stuck `weekly` field — no new endpoint.
 * Reads as a calm informational signal, not an alarm.
 *
 * Composes:
 *   <stuck-clause> — <reviews-completed-clause>, <lessons-released-clause>.
 *   Cutoffs: 60 min reviews, 4 h lessons.
 *
 * If clean-completion counts are both zero, the "—" suffix is
 * dropped so the line never reads as hollow ("0 completed cleanly").
 */
function OperationalDigestLine({ digest, compact }) {
  const weekly = digest?.weekly || {};
  const thresholds = digest?.thresholds || {};
  const total = Number(weekly.total_swept || 0);
  const reviewsClean = Number(weekly.lesson_reviews_completed_clean || 0);
  const lessonsClean = Number(weekly.work_lessons_released_clean || 0);
  const reviewMin = Number(thresholds.stuck_review_ttl_minutes || 60);
  const lessonHrs = Number(thresholds.work_lesson_abandoned_hours || 4);

  const stuckClause = total === 0
    ? "No stuck operations swept this week"
    : `Operational sweepers cleared ${total} stuck ${total === 1 ? "operation" : "operations"} this week`;

  const cleanParts = [];
  if (reviewsClean > 0) {
    cleanParts.push(
      `${reviewsClean} ${reviewsClean === 1 ? "review" : "reviews"} completed cleanly`,
    );
  }
  if (lessonsClean > 0) {
    cleanParts.push(
      `${lessonsClean} ${lessonsClean === 1 ? "lesson" : "lessons"} released normally`,
    );
  }
  const headline = cleanParts.length > 0
    ? `${stuckClause} — ${cleanParts.join(", ")}.`
    : `${stuckClause}.`;

  const cutoffs = `Cutoffs: ${reviewMin} min reviews, ${lessonHrs} h lessons.`;

  return (
    <p
      data-testid="operational-digest-line"
      data-total-swept={total}
      data-reviews-clean={reviewsClean}
      data-lessons-clean={lessonsClean}
      className={`text-[12px] leading-snug opacity-90 ${compact ? "mt-2 pt-2 border-t border-current/10" : "mt-0.5"}`}
    >
      <span className="font-bold">{headline}</span>{" "}
      <span className="opacity-75">{cutoffs}</span>
    </p>
  );
}

function CalibrationStats({ alert }) {
  const rows = [];
  if (typeof alert.pass_rate === "number") {
    rows.push(`pass ${alert.pass_rate}%`);
  }
  if (typeof alert.threshold === "number") {
    rows.push(`threshold ${alert.threshold}%`);
  }
  if (alert.failure_count > 0) {
    rows.push(`${alert.failure_count} failed`);
  }
  if (rows.length === 0) return null;
  return (
    <p
      data-testid="calibration-banner-stats"
      className="text-[11px] font-mono mt-1.5 opacity-80"
    >
      {rows.join(" · ")}
      {alert.failed_axes && alert.failed_axes.length > 0 && (
        <span className="ml-1">
          · weak axes: {alert.failed_axes.join(", ")}
        </span>
      )}
    </p>
  );
}
